import { CircularProgress, Grid, Paper, Table, TableBody, TableCell, TableRow } from "@material-ui/core";
import { useState, Fragment, useEffect } from 'react';
import ArtApi from "../../api/ArtApi";
import IEventDetails from "../../models/EventDetails"
import UserInfo from "../../components/UserInfo";
import Moment from "react-moment";
import { RouteComponentProps, useLocation } from "react-router";
import { CommentsTable } from "../../components/CommentsTable";
import { TaggingChart } from "../../components/EventChart";
import { LabelledEvents } from "../../components/LabelledEvents";
import ReviewerStatus from "../../components/ReviewerStatus";
import { DownloadMsgPack } from "../../components/DownloadMsgPack";
import { ReviewStatus } from "../../models/IReviewer";
import { useAuthDataContext } from "../../components/AuthDataProvider";

type SentreeEventDetailsProps = RouteComponentProps<{
    sentreeEventId: string;
}>;


export const SentreeEventDetails = (props: SentreeEventDetailsProps) => {
    const { user } = useAuthDataContext();

    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isDownloadable, setIsDownloadable] = useState(false);
    const [sentreeEvent, setSentreeEvent] = useState<IEventDetails | null>(null);


    // A custom hook that builds on useLocation to parse
    // the query string for you.
    function useQuery() {
    return new URLSearchParams(useLocation().search);
    }

    let query = useQuery();
    const forceReview = query.get("forceReview")

    useEffect(() => {
        setIsLoading(true);
        ArtApi.getSentreeEvent(props.match.params.sentreeEventId, forceReview)
            .then(response => {
                setIsLoading(false);
                setSentreeEvent(response.response);
                setError(response.error);
            }).catch(error => {
                setIsLoading(false);
                setError(error.message);
            })
    }, [props.match.params.sentreeEventId, forceReview]);

    useEffect(() => {
        if (sentreeEvent === null) {
            setIsDownloadable(false);
        } else {
            setIsDownloadable(sentreeEvent.completed)
        }
    }, [sentreeEvent]);


    const closeReview = (status: ReviewStatus) => {

        let currSentreeEvent = Object.assign({}, sentreeEvent);
        if(currSentreeEvent?.reviewers){
            for (const reviewer of currSentreeEvent?.reviewers) {
                if(reviewer.reviewer === user?.user_details.user_id){
                    reviewer.status = status
                    break;
                }
            }
            setSentreeEvent(currSentreeEvent);
        }
    }

    return (
        <Fragment>
            <h1>Sentree Event</h1>
            {isLoading && <CircularProgress color="secondary" />}
            {error}
            <Grid container spacing={3}>
                <Grid item>
                    <h2>Details</h2>
                    <Paper className="paper">
                        <Table>
                            <TableBody>
                                <TableRow>
                                    <TableCell>
                                        Device ID
                                    </TableCell>
                                    <TableCell>
                                        {sentreeEvent && <a href={"https://mfgweb.alertlabs.com/device/" + sentreeEvent.deviceId}>
                                            {sentreeEvent.deviceId}
                                        </a>}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>
                                        Type
                                    </TableCell>
                                    <TableCell>
                                        {sentreeEvent ? (sentreeEvent.sensorType ? sentreeEvent.sensorType : "Sentree") : "-"}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>
                                        Reported By
                                    </TableCell>
                                    <TableCell>
                                        {sentreeEvent ? <UserInfo userId={sentreeEvent.reportedBy} /> : "-"}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>
                                        Priming Start Date
                                    </TableCell>
                                    <TableCell>
                                        {sentreeEvent ? <Moment format="YYYY-MM-DD">{sentreeEvent.primingStartDate}</Moment> : "-"}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>
                                        Region of Interest Start Date
                                    </TableCell>
                                    <TableCell>
                                        {sentreeEvent ? <Moment format="YYYY-MM-DD">{sentreeEvent.regionOfInterestStartDate}</Moment> : "-"}
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>
                                        Region of Interest End Date
                                    </TableCell>
                                    <TableCell>
                                        {sentreeEvent ? <Moment format="YYYY-MM-DD">{sentreeEvent.endDate}</Moment> : "-"}
                                    </TableCell>
                                </TableRow>
                                {isDownloadable ?
                                    <TableRow>
                                        <TableCell>
                                            Download
                                    </TableCell>
                                        <TableCell>
                                            {sentreeEvent ? <DownloadMsgPack eventId={sentreeEvent._id} /> : "-"}
                                        </TableCell>
                                    </TableRow>
                                    : null}
                            </TableBody>
                        </Table>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={12} lg={6}>
                    <h2>Reviewers</h2>
                    <Paper className="paper">
                        <Table>
                            <TableBody>
                                {
                                    sentreeEvent?.reviewers.map((reviewer, i) => (
                                        <TableRow key={i}>
                                            <TableCell><UserInfo userId={reviewer.reviewer} /></TableCell>
                                            <TableCell><ReviewerStatus status={reviewer.status} /></TableCell>
                                        </TableRow>
                                    ))
                                }
                            </TableBody>
                        </Table>
                    </Paper>
                </Grid>
                <CommentsTable eventId={props.match.params.sentreeEventId} comments={sentreeEvent ? sentreeEvent.comments : []} />
                <Grid item xs={12} md={12} lg={6}>
                    <h2>Data Science</h2>
                    <Paper className="paper">
                        <Table>
                            <TableBody>
                                {
                                    sentreeEvent?.dataScientists.map((reviewer, i) => (
                                        <TableRow key={i}>
                                            <TableCell><UserInfo userId={reviewer.reviewer} /></TableCell>
                                            <TableCell><ReviewerStatus status={reviewer.status} /></TableCell>
                                        </TableRow>
                                    ))
                                }
                            </TableBody>
                        </Table>
                    </Paper>
                </Grid>
                <TaggingChart eventId={props.match.params.sentreeEventId} getChartFn={ArtApi.getSentreeChart} />
                {sentreeEvent ?
                    <LabelledEvents
                        reviewers={sentreeEvent.reviewers}
                        taggedEvents={sentreeEvent.reviewedEvents}
                        eventId={sentreeEvent._id}
                        dataScience={false}
                        archived={sentreeEvent.archived}
                        eventType='Sentree'
                        completedCallback={closeReview} /> : null}

                {sentreeEvent ?
                    <LabelledEvents
                        reviewers={sentreeEvent.dataScientists}
                        taggedEvents={sentreeEvent.dataScientestReviewedEvents}
                        eventId={sentreeEvent._id}
                        dataScience={true}
                        archived={sentreeEvent.archived}
                        eventType='Sentree'
                        completedCallback={closeReview}/> : null}
            </Grid>
        </Fragment>
    )
}
